import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PageShell from "@/components/PageShell";
import { trackEmailClick } from "@/lib/analytics";

const Privacy = () => {
  return (
    <PageShell
      title="Privacy Policy | Skandor Technologies"
      description="How Skandor Technologies collects, uses, and protects your personal information."
      canonicalPath="/privacy"
    >
      <section className="pb-16">
        <div className="container mx-auto max-w-3xl">
          <Link
            to="/"
            className="inline-flex items-center gap-2 min-h-11 text-sm text-muted-foreground hover:text-ink transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <h1 className="text-[clamp(2rem,5vw,3.25rem)] font-semibold text-ink mb-8">Privacy Policy</h1>

          <div className="max-w-none text-muted-foreground space-y-8">
            <p className="text-lg">
              <strong className="text-ink">Last updated:</strong> February 1, 2026
            </p>

            <section className="space-y-3">
              <h2 className="text-xl font-semibold text-ink">1. Introduction</h2>
              <p className="text-base">
                Skandor Technologies ("we," "our," or "us") is committed to protecting your privacy.
                This Privacy Policy explains how we collect, use, disclose, and safeguard your
                information when you visit our website or use our services.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-semibold text-ink">2. Information we collect</h2>
              <p className="text-base">We collect information you provide directly to us, including:</p>
              <ul className="list-disc pl-6 space-y-2 text-base">
                <li>Name and contact information (email, phone number)</li>
                <li>Company name and industry</li>
                <li>Country of residence</li>
                <li>Messages and inquiries you submit through our contact forms</li>
              </ul>
              <p className="text-base">
                We also automatically collect certain information when you visit our website,
                including your IP address, browser type, operating system, and pages visited.
                We use Google Analytics to help us understand how visitors use our site.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-semibold text-ink">3. How we use your information</h2>
              <ul className="list-disc pl-6 space-y-2 text-base">
                <li>Respond to your inquiries and provide requested services</li>
                <li>Send you relevant communications about our services</li>
                <li>Improve our website and services</li>
                <li>Analyze website usage and trends</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-semibold text-ink">4. Information sharing</h2>
              <p className="text-base">
                We do not sell, trade, or rent your personal information to third parties. We may
                share your information with service providers who assist us in operating our
                website and conducting our business, subject to confidentiality agreements.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-semibold text-ink">5. Data security</h2>
              <p className="text-base">
                We implement appropriate technical and organizational security measures to protect
                your personal information against unauthorized access, alteration, disclosure, or
                destruction.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-semibold text-ink">6. Your rights</h2>
              <p className="text-base">
                You have the right to access, correct, or delete your personal information. You may
                also opt out of receiving marketing communications from us at any time.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-semibold text-ink">7. Contact us</h2>
              <p className="text-base">
                Questions about this policy? Email{" "}
                <a href="mailto:Admin@skandortech.ai" onClick={() => trackEmailClick("privacy_page")} className="text-steel-2 font-semibold hover:underline underline-offset-4">
                  Admin@skandortech.ai
                </a>.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-semibold text-ink">8. Changes to this policy</h2>
              <p className="text-base">
                We may update this Privacy Policy from time to time. We will notify you of any
                changes by posting the new Privacy Policy on this page and updating the "Last
                Updated" date.
              </p>
            </section>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default Privacy;
